import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './StoringenOverzicht.css';
import api from '../services/api';
import { useMenuLabel } from '../context/AuthContext';

const StoringenOverzicht = () => {
  const storingLbl = useMenuLabel('storingen', 'Storingen', '🚨');
  const { gebruiker, logout } = useAuth();
  const navigate = useNavigate();
  const [storingen, setStoringen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('alle');
  const [maand, setMaand] = useState(new Date().toISOString().slice(0, 7));

  useEffect(() => {
    loadStoringen();
  }, [maand]);

  const loadStoringen = async () => {
    try {
      setLoading(true);
      const response = await api.get('/storingen', { params: { maand } });
      setStoringen(response.data.storingen || []);
      setLoading(false);
    } catch (error) {
      console.error('Fout bij laden storingen:', error);
      setLoading(false);
    }
  };

  const getPrioriteitColor = (prioriteit) => {
    switch (prioriteit) {
      case 'urgent': return '#ef4444';
      case 'hoog': return '#f59e0b';
      case 'normaal': return '#3b82f6';
      case 'laag': return '#10b981';
      default: return '#6b7280';
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      nieuw: { label: 'Nieuw', color: '#3b82f6' },
      doorgestuurd: { label: 'Doorgestuurd', color: '#f59e0b' },
      wacht_op_goedkeuring: { label: 'Wacht op goedkeuring', color: '#f59e0b' },
      goedgekeurd: { label: 'Goedgekeurd', color: '#10b981' },
      afgekeurd: { label: 'Afgekeurd', color: '#ef4444' },
      in_behandeling: { label: 'In behandeling', color: '#8b5cf6' },
      opgelost: { label: 'Opgelost', color: '#10b981' },
      afgerond: { label: 'Afgerond', color: '#10b981' },
      werkorder_aangemaakt: { label: 'Werkorder aangemaakt', color: '#06b6d4' }
    };
    const badge = badges[status] || { label: status, color: '#6b7280' };
    return (
      <span style={{
        background: badge.color,
        color: 'white',
        padding: '4px 12px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: '600'
      }}>
        {badge.label}
      </span>
    );
  };

  // Filter storingen op basis van tab
  const getFilteredStoringen = () => {
    const afdelingId = gebruiker?.afdeling_id;
    if (!afdelingId) return storingen;
    
    switch (activeTab) {
      case 'verzonden':
        // Storingen die IK heb doorgestuurd naar andere afdelingen
        return storingen.filter(s => 
          s.gemeld_door_afdeling_id === afdelingId && 
          s.doorgestuurd_naar_afdeling && 
          s.doorgestuurd_naar_afdeling !== afdelingId
        );
      case 'ontvangen':
        // Storingen die naar MIJ zijn doorgestuurd
        return storingen.filter(s => 
          s.doorgestuurd_naar_afdeling === afdelingId &&
          s.gemeld_door_afdeling_id !== afdelingId
        );
      case 'mijn':
        // Mijn eigen storingen (gemeld door mijn afdeling)
        return storingen.filter(s => 
          s.gemeld_door_afdeling_id === afdelingId &&
          (!s.doorgestuurd_naar_afdeling || s.doorgestuurd_naar_afdeling === afdelingId)
        );
      default:
        return storingen;
    }
  };

  const filteredStoringen = getFilteredStoringen();

  // Tel storingen per tab
  const afdelingId = gebruiker?.afdeling_id;
  
  const countVerzonden = storingen.filter(s => 
    s.gemeld_door_afdeling_id === afdelingId && 
    s.doorgestuurd_naar_afdeling && 
    s.doorgestuurd_naar_afdeling !== afdelingId
  ).length;

  const countOntvangen = storingen.filter(s => 
    s.doorgestuurd_naar_afdeling === afdelingId &&
    s.gemeld_door_afdeling_id !== afdelingId
  ).length;

  // Tel ontvangen die nog wachten op goedkeuring
  const countWachtOpGoedkeuring = storingen.filter(s => 
    s.doorgestuurd_naar_afdeling === afdelingId &&
    s.gemeld_door_afdeling_id !== afdelingId &&
    (s.status === 'doorgestuurd' || s.status === 'wacht_op_goedkeuring' || s.status === 'nieuw')
  ).length;

  if (loading) {
    return <div className="loading-screen">Laden...</div>;
  }

  return (
    <div className="storingen-overzicht">

      <div className="dashboard-container">
        <div className="dashboard-header">
          <h1>{storingLbl.icon} {storingLbl.label} Overzicht</h1>
          <div className="header-actions">
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="storingen-tabs" style={{ 
          display: 'flex', 
          gap: '10px', 
          marginBottom: '20px',
          flexWrap: 'wrap'
        }}>
          <input
            type="month"
            value={maand === 'alles' ? '' : maand}
            onChange={(e) => setMaand(e.target.value || 'alles')}
            style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #d1d5db', fontWeight: 600, color: '#374151' }}
          />
          <button
            onClick={() => setMaand('alles')}
            style={{
              padding: '10px 20px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontWeight: '600',
              background: maand === 'alles' ? '#3b82f6' : '#e5e7eb',
              color: maand === 'alles' ? 'white' : '#374151'
            }}
          >
            Alles
          </button>
          <button 
            onClick={() => setActiveTab('alle')}
            style={{
              padding: '10px 20px',
              borderRadius: '8px',
              border: 'none',
              cursor: 'pointer',
              fontWeight: '600',
              background: activeTab === 'alle' ? '#3b82f6' : '#e5e7eb',
              color: activeTab === 'alle' ? 'white' : '#374151'
            }}
          >
            Alle ({storingen.length})
          </button>
          <button 
            onClick={() => setActiveTab('mijn')}
            style={{
              padding: '10px 20px',
              borderRadius: '8px',
              border: 'none',
              cursor: 'pointer',
              fontWeight: '600',
              background: activeTab === 'mijn' ? '#3b82f6' : '#e5e7eb',
              color: activeTab === 'mijn' ? 'white' : '#374151'
            }}
          >
            Mijn storingen
          </button>
          <button 
            onClick={() => setActiveTab('verzonden')}
            style={{
              padding: '10px 20px',
              borderRadius: '8px',
              border: 'none',
              cursor: 'pointer',
              fontWeight: '600',
              background: activeTab === 'verzonden' ? '#f59e0b' : '#e5e7eb',
              color: activeTab === 'verzonden' ? 'white' : '#374151'
            }}
          >
            📤 Verzonden ({countVerzonden})
          </button>
          <button 
            onClick={() => setActiveTab('ontvangen')}
            style={{
              padding: '10px 20px',
              borderRadius: '8px',
              border: 'none',
              cursor: 'pointer',
              fontWeight: '600',
              background: activeTab === 'ontvangen' ? '#10b981' : '#e5e7eb',
              color: activeTab === 'ontvangen' ? 'white' : '#374151',
              position: 'relative'
            }}
          >
            📥 Ontvangen ({countOntvangen})
            {countWachtOpGoedkeuring > 0 && (
              <span style={{
                position: 'absolute',
                top: '-8px',
                right: '-8px',
                background: '#ef4444',
                color: 'white',
                borderRadius: '50%',
                width: '22px',
                height: '22px',
                fontSize: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 'bold'
              }}>
                {countWachtOpGoedkeuring}
              </span>
            )}
          </button>
        </div>

        {filteredStoringen.length === 0 ? (
          <div className="no-storingen">
            <p>
              {activeTab === 'verzonden' && 'Geen doorgestuurde storingen'}
              {activeTab === 'ontvangen' && 'Geen ontvangen storingen'}
              {activeTab === 'mijn' && 'Geen eigen storingen'}
              {activeTab === 'alle' && 'Geen storingen gemeld'}
            </p>
          </div>
        ) : (
          <div className="storingen-list">
            {filteredStoringen.map(storing => (
              <div
                key={storing.id}
                className="storing-card"
                onClick={() => navigate(`/storing/${storing.id}`)}
                style={{
                  borderLeft: activeTab === 'ontvangen' ? '4px solid #10b981' : 
                              activeTab === 'verzonden' ? '4px solid #f59e0b' : 'none'
                }}
              >
                <div className="storing-header">
                  <div className="storing-title">
                    <div
                      className="prioriteit-dot"
                      style={{ background: getPrioriteitColor(storing.prioriteit) }}
                    />
                    <h3>{storing.titel}</h3>
                  </div>
                  {getStatusBadge(storing.status)}
                </div>

                <p className="storing-beschrijving">{storing.beschrijving}</p>

                <div className="storing-meta">
                  <span>👤 {storing.gemeld_door_voornaam} {storing.gemeld_door_achternaam}</span>
                  {storing.melder_afdeling_naam && (
                    <span>🏢 Van: {storing.melder_afdeling_naam}</span>
                  )}
                  {storing.afdeling_naam && storing.doorgestuurd_naar_afdeling && (
                    <span>➡️ Naar: {storing.afdeling_naam}</span>
                  )}
                  {storing.locatie_adres && <span>📍 {storing.locatie_adres}</span>}
                  {storing.aantal_fotos > 0 && <span>📷 {storing.aantal_fotos} foto's</span>}
                  <span>🕐 {new Date(storing.created_at).toLocaleString('nl-NL')}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default StoringenOverzicht;
